import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { MutableRefObject, ReactNode, RefObject } from "react";

import { filterExpandableCriteriaFromComparison } from "../../helpers/comparison";
import { CRMAppLocationIdentifier } from "../../types/Faq";
import APIcon from "../buttons/APIcon";
import { Comparison, ExpandableCriteria } from "./APComparison";
import APExpandableRow from "./APComparisonExpandableCriteria";

interface APComparisonTableProps<T> {
  comparison: Comparison<T>;
  sortedItems: T[];
  itemSorter: (
    items: T[],
    criteriaId: keyof T
  ) => {
    maxValue: T[keyof T];
    allEqualValue: boolean;
    sortedItems: T[];
  };
  expandedCriteria: Record<string, boolean>;
  itemWidth: number;
  labelItemWidth: number;
  labelRefs: MutableRefObject<Record<string, HTMLElement>>;
  headerRowRef: RefObject<HTMLTableRowElement>;
  zIndex: number;
  onSetSortCriteria: (criteriaId: keyof T) => void;
  onToggleExpandedCriteria: (expandableCriteriaId: string) => void;
}

const APComparisonTable = <T,>({
  comparison,
  sortedItems,
  itemSorter,
  expandedCriteria,
  itemWidth,
  labelItemWidth,
  labelRefs,
  headerRowRef,
  zIndex,
  onSetSortCriteria,
  onToggleExpandedCriteria,
}: APComparisonTableProps<T>) => {
  const topCriteria: (keyof T)[] = [
    ...comparison.selectedCriteria,
    comparison.initialPrimaryCriteria,
    ...(comparison.summaryCriteria &&
    comparison.summaryCriteria !== comparison.initialPrimaryCriteria
      ? [comparison.summaryCriteria]
      : []),
    ...(comparison.alwaysShownTopCriteria ?? []),
  ];
  const bottomCriteria: (keyof T)[] = [
    ...(comparison.alwaysShownBottomCriteria ?? []).filter(
      (c) => !topCriteria.includes(c)
    ),
  ];
  const expandableCriteria = filterExpandableCriteriaFromComparison(comparison);
  const renderAppLocation = comparison.faqProps?.renderAppLocation ?? undefined;

  const renderFieldWrapper = (
    key: string,
    criteriaId: keyof T,
    content: ReactNode,
    isLabel: boolean = false,
    appLocationId?: CRMAppLocationIdentifier,
    topAlign?: boolean,
    backgroundColor?: string
  ) => {
    const _content = (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: isLabel ? "flex-start" : "center",
          p: 1,
          width: "100%",
        }}
      >
        {content}
      </Box>
    );

    return (
      <TableCell
        key={key}
        ref={(el: HTMLElement) => {
          if (el) labelRefs.current[String(criteriaId)] = el;
        }}
        sx={{
          minWidth: isLabel ? `${labelItemWidth}px` : `${itemWidth}px`,
          maxWidth: isLabel ? `${labelItemWidth}px` : `${itemWidth}px`,
          position: isLabel ? "sticky" : undefined,
          backgroundColor: isLabel ? backgroundColor ?? "white" : undefined,
          left: isLabel ? 0 : undefined,
          zIndex: isLabel ? zIndex : undefined,
          py: 1,
          px: 0,
          verticalAlign: isLabel || topAlign ? "top" : "center",
          boxShadow: isLabel ? "-2px 0 0 0 white" : undefined,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: isLabel ? "flex-start" : "center",
          }}
        >
          {appLocationId && renderAppLocation
            ? renderAppLocation(appLocationId, _content)
            : _content}
        </Box>
      </TableCell>
    );
  };

  const renderFieldLabel = (criteriaId: keyof T) => {
    const criterion = comparison.criteria[criteriaId];
    const label = criterion?.label;
    const labelEndAdornment = criterion?.labelEndAdornment;
    const labelAppLocationId = criterion?.labelAppLocationId;
    const isSortable = criterion?.sortable;

    const labelContent = criterion?.labelRenderer ? (
      criterion.labelRenderer()
    ) : (
      <Typography
        variant="labelLarge"
        color={isSortable || labelAppLocationId ? "primary" : undefined}
        onClick={() => {
          isSortable && onSetSortCriteria(criteriaId);
        }}
        sx={{ cursor: isSortable ? "pointer" : undefined }}
      >
        {label}
      </Typography>
    );

    return renderFieldWrapper(
      `${String(criteriaId)}-label`,
      criteriaId,
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: 0.5,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: 1,
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {labelAppLocationId && renderAppLocation
            ? renderAppLocation(labelAppLocationId, labelContent)
            : labelContent}
          {labelEndAdornment &&
            (labelAppLocationId && renderAppLocation
              ? renderAppLocation(labelAppLocationId, labelEndAdornment)
              : labelEndAdornment)}
        </Box>
      </Box>,
      true
    );
  };

  const renderExpandableLabel = (
    id: string,
    label: string,
    expanded: boolean
  ) => {
    return renderFieldWrapper(
      `${String(id)}-label`,
      id as keyof T,
      <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.5 }}>
        <Typography
          variant="labelLarge"
          color="primary"
          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {label}
        </Typography>
        <APIcon name={expanded ? "arrow-up" : "arrow-down"} />
      </Box>,
      true,
      undefined,
      undefined,
      "neutralGray.light"
    );
  };

  const renderValue = (
    index: number,
    item: T,
    criteriaId: keyof T,
    maxValue: unknown,
    allEqualValue: boolean
  ) => {
    const criterion = comparison.criteria[criteriaId];
    const value = item[criteriaId];

    let color: "secondary.main" | "outline.main" | undefined = undefined;
    let fontWeight: "600" | undefined = undefined;

    if (allEqualValue) {
      color = "outline.main";
    } else {
      const { highlightWinner = true } = criterion?.sortable ?? {};
      const isMax = criterion?.sortable ? value === maxValue : false;

      if (isMax && highlightWinner) {
        color = "secondary.main";
        fontWeight = "600";
      }
    }

    return renderFieldWrapper(
      `${String(criteriaId)}-${index}-value`,
      criteriaId,
      criterion?.valueRenderer ? (
        criterion.valueRenderer(item)
      ) : (
        <Typography
          variant={criterion?.styles?.smallFont ? "labelXSmall" : "labelLarge"}
          fontWeight={fontWeight}
          sx={{ color: color }}
        >
          {criterion?.valueFormatter
            ? criterion.valueFormatter(item[criteriaId])
            : String(item[criteriaId])}
        </Typography>
      ),
      false,
      criterion?.getAppLocationId
        ? criterion.getAppLocationId(item, index)
        : undefined,
      criterion?.styles?.topAlign
    );
  };

  const renderCriteriaRow = (
    items: T[],
    criteriaId: keyof T,
    isHeader: boolean = false
  ) => {
    const { maxValue, allEqualValue } = itemSorter(items, criteriaId);

    return (
      <TableRow
        key={`${String(criteriaId)}-row`}
        ref={isHeader ? headerRowRef : undefined}
      >
        {renderFieldLabel(criteriaId)}
        {items.map((item, index) =>
          renderValue(index, item, criteriaId, maxValue, allEqualValue)
        )}
      </TableRow>
    );
  };

  const renderExpandableCriteriaRow = (
    items: T[],
    expandableCriteria: ExpandableCriteria<T>
  ) => {
    return (
      <APExpandableRow
        id={expandableCriteria.id}
        key={expandableCriteria.id}
        labelRenderer={(expanded: boolean) =>
          renderExpandableLabel(
            expandableCriteria.id,
            expandableCriteria.label,
            expanded
          )
        }
        numberOfColumns={items.length}
        expanded={expandedCriteria[expandableCriteria.id]}
        onExpandToggle={() => onToggleExpandedCriteria(expandableCriteria.id)}
      >
        {expandableCriteria.criteria.map((criterion) =>
          renderCriteriaRow(items, criterion)
        )}
      </APExpandableRow>
    );
  };

  return (
    <Table stickyHeader sx={{ pb: 2 }}>
      <TableBody>
        {comparison.headerCriteria &&
          renderCriteriaRow(sortedItems, comparison.headerCriteria, true)}
        {Object.values(topCriteria).map((criterion) =>
          renderCriteriaRow(sortedItems, criterion)
        )}
        {expandableCriteria.map((expandableCriteria) =>
          renderExpandableCriteriaRow(sortedItems, expandableCriteria)
        )}
        {Object.values(bottomCriteria).map((criterion) =>
          renderCriteriaRow(sortedItems, criterion)
        )}
      </TableBody>
    </Table>
  );
};

export default APComparisonTable;
